/* eslint-disable @typescript-eslint/no-explicit-any */
import { catchError, timeout } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';

import { ErrorHandlerService } from '../services/error-handler.service';
import * as Sentry from '@sentry/angular';

// TODO
// refactor with Context Header for custom error handling

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
// Long tokens with at least one digit: Gmail message and thread ids, Firebase uids.
const OPAQUE_ID = /^(?=.*\d)[\w-]{16,}$/;

/**
 * Reduces a request URL to its route shape, e.g. `/sup/listing/:id/requests/:id`,
 * so every failure of one endpoint groups into one Sentry issue.
 */
export function endpointPattern(url: string): string {
  const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0];
  return path
    .split('/')
    .map(segment => (UUID.test(segment) || NUMERIC.test(segment) || OPAQUE_ID.test(segment) ? ':id' : segment))
    .join('/');
}

@Injectable({
  providedIn: 'root'
})
export class ErrorsInterceptor implements HttpInterceptor {
  private readonly ignoreErrors: string[] = ['activity-history'];

  constructor(private _errorHandlerService: ErrorHandlerService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let requestTimeout = 30000;
    if (req.headers.get('skipTimeout')) {
      requestTimeout = 120000;
    }
    return next.handle(req).pipe(
      timeout(requestTimeout),
      catchError<HttpEvent<any>, any>(error => this.handleHttpError(req, error))
    );
  }

  private handleHttpError(req: HttpRequest<any>, error: any): Observable<any> {
    if (this.shouldDisplayError(req.url)) {
      this._errorHandlerService.handleError(error?.status, error?.error?.message);
    }

    // Only server errors are bugs worth an issue. 4xx responses are the API rejecting
    // the request as designed, and client timeouts are slow networks; neither is
    // actionable from here.
    if (error instanceof HttpErrorResponse && error.status >= 500) {
      this.reportServerError(req, error);
    }

    return throwError(error);
  }

  private reportServerError(req: HttpRequest<any>, error: HttpErrorResponse): void {
    const endpoint = endpointPattern(req.url);
    Sentry.captureMessage(`${req.method} ${endpoint} returned ${error.status}`, {
      level: 'error',
      fingerprint: ['http-server-error', req.method, endpoint, String(error.status)],
      tags: { 'http.status_code': String(error.status) },
      extra: { url: req.url }
    });
  }

  shouldDisplayError(url: string): boolean {
    return this.ignoreErrors.filter(path => url.includes(path)).length === 0;
  }
}
